import { useMemo, useState } from 'react';

import { TagBadge } from '@/components/contacts/TagBadge';
import { useTagDefinitions } from '@/hooks/useTagDefinitions';

import { mergeTagsWithTypes } from '@repo/shared-types';

const FIELD_CLASS =
  'focus:ring-primary w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none';

/**
 * Tag multi-select backed by the tag catalog. Adding a typed tag replaces any
 * selected tag of the same type (server enforces the same rule). Free-text
 * tags are allowed — they just have no type or color until defined.
 */
export const TagPicker = ({
  value,
  onChange,
  placeholder = 'Add tag and press Enter...',
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}) => {
  const { definitions, byName } = useTagDefinitions();
  const [input, setInput] = useState('');

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed) return;
    onChange(mergeTagsWithTypes(value, [trimmed], definitions));
    setInput('');
  };

  const suggestions = useMemo(
    () =>
      definitions.filter(
        (d) =>
          !value.includes(d.name) && (!input || d.name.toLowerCase().includes(input.toLowerCase())),
      ),
    [definitions, value, input],
  );

  return (
    <div className="space-y-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {value.map((tag) => (
            <TagBadge
              key={tag}
              name={tag}
              definition={byName.get(tag)}
              onRemove={() => onChange(value.filter((t) => t !== tag))}
            />
          ))}
        </div>
      )}
      <input
        className={FIELD_CLASS}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addTag(input);
          }
        }}
        placeholder={placeholder}
      />
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {suggestions.slice(0, 12).map((def) => (
            <button
              key={def.name}
              type="button"
              onClick={() => addTag(def.name)}
              className="cursor-pointer rounded-full border border-dashed px-2 py-0.5 text-[11px] transition-colors hover:brightness-110"
              style={{ borderColor: def.color || '#9ca3af', color: def.color || '#9ca3af' }}
            >
              + {def.name}
              {def.type?.trim() && <span className="opacity-60"> · {def.type.trim()}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
